import React from 'react'
import Dayzed from 'dayzed'
import moment from 'moment'
import { css, cx } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { I18nConsumer } from '../../components/i18n-provider'
import {
  DayzedRenderProps,
  DayzedDate,
  DateRange,
  TimeRange,
  getValidTimeRangeDefaultToStart,
  getValidTimeRangeDefaultToEnd,
  dateToToLongMonthString,
  getDayAbbreviations,
  DateOrNow,
  getDateFromDateOrNow,
  getValidHour,
  getValidMinute,
  isValidEndDate
} from './shared'
import {
  Month,
  MonthHeader,
  Week,
  CalendarBackButton,
  CalendarForwardButton
} from './calendar'
import { TimePicker } from '.'
import { Button } from '@contrast-security-inc/design-system-buttons'

export const applyButtonTestID = 'apply-button-test-id'
export const cancelButtonTestID = 'cancel-button-test-id'
export const startTimeTestID = 'start-time-test-id'
export const endTimeTestID = 'end-time-test-id'

export interface DateRangePickerProps {
  /**
   * Do not display time inputs
   */
  readonly hideTime?: boolean
  /**
   * Disable apply button if range is >= value,
   * default = 0 allowing ranges within a single day
   */
  readonly minRangeDays?: number
  /**
   * An initial range to populate the selector
   */
  readonly initialRange?: DateRange

  /**
   * The minimum date allowed - defaults to no value
   */
  readonly minDate?: DateOrNow

  /**
   * The maximum date allowed - defaults to no value
   */
  readonly maxDate?: DateOrNow

  /**
   * The date used to calculate what month to show initially - default to current date
   */
  readonly initialMonth?: DateOrNow

  readonly onCancel: () => void
  readonly onApply: (range: DateRange) => void
}

interface State {
  readonly startDate?: Date
  readonly endDate?: Date
  readonly hoveredDate?: Date
  readonly startHour: number
  readonly startMinute: number
  readonly endHour: number
  readonly endMinute: number
  readonly offset: number
}

export class DateRangePicker extends React.Component<
  DateRangePickerProps,
  State
> {
  constructor(props: DateRangePickerProps) {
    super(props)

    this.state = this.getInitialStateFromProps()
  }

  public render() {
    const {
      startDate,
      endDate,
      startHour,
      startMinute,
      endHour,
      endMinute,
      offset
    } = this.state
    const {
      hideTime,
      minRangeDays = 0,
      minDate,
      maxDate,
      initialMonth = 'NOW'
    } = this.props

    const selected =
      startDate && endDate ? [startDate, endDate] : startDate ? [startDate] : []
    const isValidRange =
      startDate &&
      endDate &&
      moment.duration(moment(endDate).diff(moment(startDate))).asDays() >=
        minRangeDays

    return (
      <div
        data-testid="date-range-picker"
        className={css`
          display: flex;
          flex-direction: column;
          width: 452px;
          line-height: initial;
          margin: 8px;
        `}
      >
        <Dayzed
          date={getDateFromDateOrNow(initialMonth)}
          minDate={minDate ? getDateFromDateOrNow(minDate) : undefined}
          maxDate={maxDate ? getDateFromDateOrNow(maxDate) : undefined}
          monthsToDisplay={2}
          offset={offset}
          onOffsetChanged={this.onOffsetChanged}
          selected={selected}
          onDateSelected={this.onDateSelected}
          render={({
            calendars,
            getDateProps,
            getBackProps,
            getForwardProps
          }: DayzedRenderProps) => {
            return (
              <div
                className={css`
                  display: flex;
                  position: relative;
                `}
              >
                <CalendarBackButton
                  getButtonProps={(props) =>
                    getBackProps({ ...props, calendars })
                  }
                />
                {calendars.map((calendar, calendarIndex) => {
                  return (
                    <Month key={`${calendar.month}-${calendar.year}`}>
                      <I18nConsumer>
                        {({ intl: { locale } }) => {
                          return (
                            <MonthHeader
                              month={dateToToLongMonthString(
                                calendar.firstDayOfMonth,
                                locale
                              )}
                              year={calendar.year.toString()}
                              dayAbbreviations={getDayAbbreviations(locale)}
                            />
                          )
                        }}
                      </I18nConsumer>
                      {calendar.weeks.map((week, weekIndex) => {
                        const key = `${calendar.year}-${calendar.month}-${weekIndex}`
                        return (
                          <Week
                            key={key}
                            week={week}
                            getDateProps={getDateProps}
                            isDateSelected={this.isDateSelected}
                            isDateHovered={this.isDateHovered}
                            isDateBeginningOfSequence={
                              this.isDateBeginningOfSequence
                            }
                            isDateEndOfSequence={this.isDateEndOfSequence}
                            onDateMouseEnter={this.onDateMouseEnter}
                            onDateMouseLeave={this.onDateMouseLeave}
                          />
                        )
                      })}
                      {!hideTime && (
                        <>
                          {calendarIndex === 0 && (
                            <FormattedMessage id="title_from">
                              {(text) => {
                                return (
                                  <div
                                    className={cx(
                                      css`
                                        display: flex;
                                        align-items: center;
                                        margin: 8px 0 8px 4px;
                                      `,
                                      {
                                        [css`
                                          margin-top: 39px;
                                        `]: calendar.weeks.length < 6
                                      }
                                    )}
                                  >
                                    <div
                                      className={css`
                                        margin-right: 4px;
                                      `}
                                    >
                                      {text}:
                                    </div>
                                    <TimePicker
                                      hour={startHour}
                                      minute={startMinute}
                                      onHourChange={this.onStartHourChange}
                                      onMinuteChange={this.onStartMinuteChange}
                                      data-testid={startTimeTestID}
                                    />
                                  </div>
                                )
                              }}
                            </FormattedMessage>
                          )}
                          {calendarIndex === 1 && (
                            <FormattedMessage id="title_until">
                              {(text) => {
                                return (
                                  <div
                                    className={cx(
                                      css`
                                        display: flex;
                                        align-items: center;
                                        margin: 8px 0 8px 4px;
                                      `,
                                      {
                                        [css`
                                          margin-top: 39px;
                                        `]: calendar.weeks.length < 6
                                      }
                                    )}
                                  >
                                    <div
                                      className={css`
                                        margin-right: 4px;
                                      `}
                                    >
                                      {text}:
                                    </div>
                                    <TimePicker
                                      hour={endHour}
                                      minute={endMinute}
                                      onHourChange={this.onEndHourChange}
                                      onMinuteChange={this.onEndMinuteChange}
                                      data-testid={endTimeTestID}
                                    />
                                  </div>
                                )
                              }}
                            </FormattedMessage>
                          )}
                        </>
                      )}
                    </Month>
                  )
                })}
                <CalendarForwardButton
                  getButtonProps={(props) =>
                    getForwardProps({ ...props, calendars })
                  }
                />
              </div>
            )
          }}
        />
        <div
          className={css`
            display: flex;
            justify-content: flex-end;
            margin-right: 20px;
            margin-bottom: 20px;
          `}
        >
          <FormattedMessage id="action_cancel">
            {(text) => {
              return (
                <Button
                  variant="secondary"
                  data-testid={cancelButtonTestID}
                  onClick={this.onCancel}
                  className={css`
                    margin-right: 8px;
                  `}
                >
                  {text}
                </Button>
              )
            }}
          </FormattedMessage>
          <FormattedMessage id="action_apply">
            {(text) => {
              return (
                <Button
                  variant="primary"
                  data-testid={applyButtonTestID}
                  onClick={this.onApply}
                  isDisabled={!isValidRange}
                >
                  {text}
                </Button>
              )
            }}
          </FormattedMessage>
        </div>
      </div>
    )
  }

  private getInitialStateFromProps = (): State => {
    const { initialRange, minDate, maxDate } = this.props

    if (
      minDate &&
      maxDate &&
      !(getDateFromDateOrNow(minDate) < getDateFromDateOrNow(maxDate))
    ) {
      throw new Error(`minDate must be less than maxDate`)
    }

    if (initialRange) {
      const { start, end } = initialRange

      if (start >= end) {
        throw new Error(`initialRange.start must be less than initialRange.end`)
      }

      if (minDate && getDateFromDateOrNow(minDate) > start) {
        throw new Error(
          `initialRange.start must be greater than or equal to minDate`
        )
      }

      if (maxDate && isValidEndDate(maxDate, end)) {
        throw new Error(
          `initialRange.end must be less than or equal to maxDate`
        )
      }

      return {
        offset: -1,
        startDate: new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate()
        ),
        endDate: new Date(end.getFullYear(), end.getMonth(), end.getDate()),
        startHour: start.getHours(),
        startMinute: start.getMinutes(),
        endHour: end.getHours(),
        endMinute: end.getMinutes()
      }
    }
    return {
      offset: -1,
      startDate: undefined,
      endDate: undefined,
      startHour: 0,
      startMinute: 0,
      endHour: 23,
      endMinute: 59
    }
  }

  private onCancel = () => {
    this.setState(this.getInitialStateFromProps(), () => {
      if (this.props.onCancel) {
        this.props.onCancel()
      }
    })
  }

  private onApply = () => {
    const { startDate, endDate, startHour, endHour, startMinute, endMinute } =
      this.state
    if (startDate && endDate && this.props.onApply) {
      this.props.onApply({
        start: new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          startHour,
          startMinute
        ),
        end: new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          endHour,
          endMinute
        )
      })
    }
  }

  private onDateSelected = ({ date, selectable }: DayzedDate) => {
    if (selectable) {
      this.setState(
        ({
          startDate: prevStartDate,
          endDate: prevEndDate,
          startHour,
          startMinute,
          endHour,
          endMinute
        }) => {
          const timeRange = { startHour, startMinute, endHour, endMinute }

          const newSelectionIsEqualToEndDate =
            prevEndDate && prevEndDate.getTime() === date.getTime()

          if (newSelectionIsEqualToEndDate) {
            return {
              startDate: undefined,
              endDate: undefined,
              ...timeRange
            }
          }

          const selectedIsAfterCurrentStartDate =
            prevStartDate && prevStartDate < date

          return {
            startDate: selectedIsAfterCurrentStartDate ? prevStartDate : date,
            endDate: date,
            ...(selectedIsAfterCurrentStartDate
              ? timeRange
              : getValidTimeRangeDefaultToStart(timeRange))
          }
        }
      )
    }
  }

  private isDateSelected = ({ selected, date }: DayzedDate): boolean => {
    const { startDate, endDate } = this.state

    return (
      selected ||
      (startDate && endDate ? date > startDate && date < endDate : false)
    )
  }

  private isDateHovered = ({ selectable, date }: DayzedDate): boolean => {
    const { startDate, endDate, hoveredDate } = this.state
    return (
      selectable &&
      (hoveredDate
        ? hoveredDate.getTime() === date.getTime() ||
          (startDate
            ? (hoveredDate > date && date > startDate) ||
              (!endDate ? hoveredDate < date && date < startDate : false)
            : false)
        : false)
    )
  }

  private isDateBeginningOfSequence = (date: DayzedDate): boolean => {
    const { startDate } = this.state
    const selected = this.isDateSelected(date)
    const previousDay = {
      ...date,
      date: new Date(date.date.getTime() - 1000 * 60 * 60 * 24)
    }
    return (
      (startDate && selected && date.date.getTime() === startDate.getTime()) ||
      (!selected &&
        this.isDateHovered(date) &&
        !(this.isDateHovered(previousDay) || this.isDateSelected(previousDay)))
    )
  }

  private isDateEndOfSequence = (date: DayzedDate): boolean => {
    const { endDate } = this.state
    const selected = this.isDateSelected(date)
    const nextDay = {
      ...date,
      date: new Date(date.date.getTime() + 1000 * 60 * 60 * 24)
    }
    return (
      (endDate &&
        selected &&
        date.date.getTime() === endDate.getTime() &&
        !this.isDateHovered(nextDay)) ||
      (!selected &&
        this.isDateHovered(date) &&
        !(this.isDateHovered(nextDay) || this.isDateSelected(nextDay)))
    )
  }

  private onDateMouseEnter = ({ date, selectable }: DayzedDate) => {
    this.setState({
      hoveredDate: selectable ? date : undefined
    })
  }

  private onDateMouseLeave = ({ date }: DayzedDate) => {
    this.setState(({ hoveredDate: prevHoveredDate }) => {
      return {
        hoveredDate:
          prevHoveredDate && prevHoveredDate.getTime() === date.getTime()
            ? undefined
            : prevHoveredDate
      }
    })
  }

  private onStartHourChange = (startHour: number) =>
    this.updateTime({ startHour }, getValidTimeRangeDefaultToStart)

  private onStartMinuteChange = (startMinute: number) =>
    this.updateTime({ startMinute }, getValidTimeRangeDefaultToStart)

  private onEndHourChange = (endHour: number) =>
    this.updateTime({ endHour }, getValidTimeRangeDefaultToEnd)

  private onEndMinuteChange = (endMinute: number) =>
    this.updateTime({ endMinute }, getValidTimeRangeDefaultToEnd)

  private updateTime = (
    newTimeRange: Partial<TimeRange>,
    timeRangeCorrector: (timeRange: TimeRange) => TimeRange
  ) => {
    this.setState((prevState) => {
      const { startDate, endDate, startHour, startMinute, endHour, endMinute } =
        prevState

      const validNewTimeRange: TimeRange = {
        startHour: getValidHour(
          newTimeRange.startHour !== undefined
            ? newTimeRange.startHour
            : startHour
        ),
        startMinute: getValidMinute(
          newTimeRange.startMinute !== undefined
            ? newTimeRange.startMinute
            : startMinute
        ),
        endHour: getValidHour(
          newTimeRange.endHour !== undefined ? newTimeRange.endHour : endHour
        ),
        endMinute: getValidMinute(
          newTimeRange.endMinute !== undefined
            ? newTimeRange.endMinute
            : endMinute
        )
      }

      if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
        return { ...timeRangeCorrector({ ...validNewTimeRange }) }
      }
      return { ...validNewTimeRange }
    })
  }

  private onOffsetChanged = (offset: number) => {
    this.setState({ offset })
  }
}
